import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { useState } from "react";
import { X } from "lucide-react";
import { Navigation } from "@/components/Navigation";
import { Footer } from "@/components/Footer";
import FloatingADUWizard from "@/components/FloatingADUWizard";
import CanonicalTag from "@/components/CanonicalTag";
import homeTheaterImage from "@/assets/home-theater-adu.jpg";

const HowToUseADUHomeTheater = () => {
  const [showCalendly, setShowCalendly] = useState(false);
  const openCalendlyModal = () => setShowCalendly(true);
  const closeCalendlyModal = () => setShowCalendly(false);

  return (
    <div className="min-h-screen bg-background text-foreground">
      <CanonicalTag url="https://www.udaadu.com/how-to-use-adu-home-theater" />
      <Navigation />
        {/* Hero Section */}
        <section className="relative overflow-hidden py-20 px-4">
          <div className="container mx-auto max-w-6xl">
            <div className="grid lg:grid-cols-2 gap-12 items-center">
              <div className="space-y-8">
                <h1 className="text-4xl lg:text-6xl font-bold leading-tight" style={{ color: '#E4683B' }}>
                  Build the Ultimate Backyard Cinema
                </h1>
                <p className="text-xl text-white leading-relaxed">
                  Transform your ADU into a private theater with movie-quality sound & visuals.
                </p>
                <div className="flex flex-col sm:flex-row gap-4">
                  <Button 
                    size="lg" 
                    className="bg-primary text-primary-foreground hover:bg-primary/90"
                    onClick={openCalendlyModal}
                  >
                    📅 Book & Schedule
                  </Button>
                </div>
              </div>
              <div className="relative">
                <img 
                  src={homeTheaterImage} 
                  alt="High-end home theater ADU with projection screen and luxury seating" 
                  className="rounded-lg shadow-2xl w-full h-auto"
                />
              </div>
            </div>
          </div>
        </section>

        {/* Design Features Section */}
        <section className="py-16 px-4">
          <div className="container mx-auto max-w-6xl">
            <h2 className="text-3xl font-bold mb-12 text-center" style={{ color: '#E4683B' }}>
              Design That Delivers
            </h2>
            <div className="grid md:grid-cols-3 gap-8">
              <Card>
                <CardContent className="p-6">
                  <h3 className="text-xl font-semibold mb-4 text-foreground">4K Projection</h3>
                  <p className="text-muted-foreground">Large projection wall or premium screen TV for cinema-quality viewing experience.</p>
                </CardContent>
              </Card>
              <Card>
                <CardContent className="p-6">
                  <h3 className="text-xl font-semibold mb-4 text-foreground">Surround Sound</h3>
                  <p className="text-muted-foreground">Professional audio system with blackout curtains for optimal acoustics and ambiance.</p>
                </CardContent>
              </Card>
              <Card>
                <CardContent className="p-6">
                  <h3 className="text-xl font-semibold mb-4 text-foreground">Premium Seating</h3>
                  <p className="text-muted-foreground">Tiered seating arrangement or luxury recliners for maximum comfort during long viewings.</p>
                </CardContent>
              </Card>
            </div>
          </div>
        </section>

        {/* Entertainment Benefits */}
        <section className="py-16 px-4 bg-muted/10">
          <div className="container mx-auto max-w-4xl text-center">
            <h2 className="text-3xl font-bold mb-8" style={{ color: '#E4683B' }}>
              Entertainment You'll Love
            </h2>
            <div className="grid md:grid-cols-2 gap-8 text-left">
              <div>
                <h3 className="text-xl font-semibold mb-4 text-foreground">Cinematic Movie Nights</h3>
                <p className="text-muted-foreground">Host private screenings anytime without theater crowds or schedules.</p>
              </div>
              <div>
                <h3 className="text-xl font-semibold mb-4 text-foreground">Gaming & Streaming</h3>
                <p className="text-muted-foreground">Perfect space for immersive gaming, sports viewing, and binge-watch sessions.</p>
              </div>
            </div>
            <div className="mt-12 flex flex-col sm:flex-row gap-4 justify-center">
              <Button 
                size="lg"
                className="bg-white text-[#E4683B] hover:bg-white/90"
                onClick={() => window.location.href = '/landing'}
              >
                📐 What fits my lot?
              </Button>
              <Button 
                size="lg"
                className="bg-[#E4683B] text-white hover:bg-[#E4683B]/90"
                onClick={() => window.open('https://udaadu.com/price-roi-report', '_blank')}
              >
                💰 Get Price & ROI Report
              </Button>
            </div>
          </div>
        </section>

        {/* Value & ROI Section */}
        <section className="py-16 px-4">
          <div className="container mx-auto max-w-6xl">
            <h2 className="text-3xl font-bold mb-12 text-center" style={{ color: '#E4683B' }}>
              Value & ROI
            </h2>
            <div className="grid md:grid-cols-2 gap-8">
              <div className="text-center">
                <h3 className="text-xl font-semibold mb-4 text-foreground">Lifestyle & Resale Value</h3>
                <p className="text-muted-foreground">Premium entertainment amenity significantly enhances property value and market appeal.</p>
              </div>
              <div className="text-center">
                <h3 className="text-xl font-semibold mb-4 text-foreground">High-Impact Feature</h3>
                <p className="text-muted-foreground">Home theaters are consistently rated as top features desired by homebuyers.</p>
              </div>
            </div>
          </div>
        </section>

        {/* Final CTA Section */}
        <section className="py-16 px-4 bg-primary/10">
          <div className="container mx-auto max-w-4xl text-center">
            <h2 className="text-3xl font-bold mb-8 text-white">
              Make your ADU the place everyone wants to be—stylish, private, unforgettable.
            </h2>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Button 
                size="lg"
                className="bg-[#E4683B] text-white hover:bg-[#E4683B]/90"
                onClick={() => window.open('/our-work', '_blank')}
              >
                🏡 Our Portfolio
              </Button>
              <Button 
                size="lg"
                className="bg-[#E4683B] text-white hover:bg-[#E4683B]/90"
                onClick={() => window.open('https://udaadu.com/faq', '_blank')}
              >
                ❓ Our FAQ
              </Button>
            </div>
          </div>
        </section>

        {/* Calendly Modal */}
        {showCalendly && (
          <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50 p-4">
            <div className="relative w-full max-w-2xl h-[70vh] bg-white rounded-lg overflow-hidden shadow-xl">
              <button 
                onClick={closeCalendlyModal} 
                className="absolute top-4 right-4 z-10 p-2 bg-white rounded-full shadow-md hover:bg-gray-100 transition-colors"
              >
                <X className="h-6 w-6 text-gray-600" />
              </button>
              <iframe 
                src="https://calendly.com/udaadu?embed=true&hide_gdpr_banner=1" 
                width="100%" 
                height="100%" 
                frameBorder="0" 
                title="Schedule a meeting" 
              />
            </div>
          </div>
        )}
        
        <Footer />
        <FloatingADUWizard />
      </div>
  );
};

export default HowToUseADUHomeTheater;